import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Search, Filter, ArrowUp, ArrowDown, Calendar, User, ChevronLeft, ChevronRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ChatOption from "@/components/ChatOption";
import MetaTags from "@/components/meta/MetaTags";
import { Article, ArticleStatus } from "@/types/article-types";
import { supabase } from "@/integrations/supabase/client";
import { formatDate } from "@/lib/date-utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useIsMobile } from "@/hooks/use-mobile";

type SortField = "publish_date" | "title" | "views";
type SortOrder = "asc" | "desc";

const ITEMS_PER_PAGE = 6;

const Blog = () => {
  const isMobile = useIsMobile();
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<SortField>("publish_date");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('articles')
          .select(`
            *,
            author:author_id (
              id,
              first_name,
              last_name,
              profile_image_url
            ),
            tags:article_tags_map (
              tag:tag_id (
                id,
                name,
                slug
              )
            )
          `)
          .eq('status', 'published')
          .order('publish_date', { ascending: false });

        if (error) throw error;

        const transformedData = data.map(item => ({
          ...item,
          status: item.status as ArticleStatus,
          tags: (item.tags || [])
            .filter((tagItem: any) => tagItem && tagItem.tag)
            .map((tagItem: any) => tagItem.tag)
        }));

        setArticles(transformedData);
        setFilteredArticles(transformedData);
      } catch (err: any) {
        console.error("Error fetching articles:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  useEffect(() => {
    let result = [...articles];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(article =>
        article.title.toLowerCase().includes(query) ||
        (article.lead_text && article.lead_text.toLowerCase().includes(query)) ||
        (article.author && 
          (
            article.author.first_name.toLowerCase().includes(query) ||
            (article.author.last_name && article.author.last_name.toLowerCase().includes(query))
          )
        )
      );
    }
    
    result.sort((a, b) => {
      let fieldA: any;
      let fieldB: any;

      switch (sortField) {
        case "publish_date":
          fieldA = new Date(a.publish_date || a.created_at).getTime();
          fieldB = new Date(b.publish_date || b.created_at).getTime();
          break;
        case "title":
          fieldA = a.title.toLowerCase();
          fieldB = b.title.toLowerCase();
          break;
        case "views":
          fieldA = a.views || 0;
          fieldB = b.views || 0;
          break;
        default:
          fieldA = new Date(a.publish_date || a.created_at).getTime();
          fieldB = new Date(b.publish_date || b.created_at).getTime();
      }

      if (sortOrder === "asc") {
        return fieldA > fieldB ? 1 : -1;
      } else {
        return fieldA < fieldB ? 1 : -1;
      }
    });
    
    setFilteredArticles(result);
    setCurrentPage(1);
  }, [articles, searchQuery, sortField, sortOrder]);

  const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedArticles = filteredArticles.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const truncateText = (text: string | undefined, maxLength: number): string => {
    if (!text) return "";
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl font-bold text-corporate-primary-100 mb-4">
                Blog
              </h1>
              <p className="text-lg text-gray-600">Loading articles...</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array(6).fill(0).map((_, i) => (
                <Card key={i} className="h-96 animate-pulse">
                  <div className="h-40 bg-gray-200 rounded-t-lg"></div>
                  <CardHeader>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-4/5"></div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="container mx-auto px-4 py-12">
          <div className="max-w-6xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-corporate-primary-100 mb-4">
              Blog
            </h1>
            <div className="py-12 text-center">
              <h2 className="text-xl font-medium text-red-600 mb-2">Error Loading Articles</h2>
              <p className="text-gray-600">{error}</p>
              <Button 
                className="mt-4"
                onClick={() => window.location.reload()}
              >
                Try Again
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <MetaTags 
        customTitle="Blog | METRADE Knowledge Hub"
        customDescription="Explore the latest articles, insights, and market analyses from crypto experts and industry leaders."
        customImage="/lovable-uploads/bf9cdc94-db08-4840-bae6-869c2f0c41bb.png"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <Link 
              to="/knowledge-hub" 
              className="inline-flex items-center text-corporate-primary-75 hover:text-corporate-secondary-100 mb-4"
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Back to Knowledge Hub
            </Link>
            <h1 className="text-3xl md:text-4xl font-bold text-corporate-primary-100 mb-4">
              Blog
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore the latest articles, insights, and market analyses from our crypto experts and industry leaders.
            </p>
          </motion.div>

          <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
            <div className="w-full md:w-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  className="pl-10 w-full md:w-80"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                className={sortField === "publish_date" ? "bg-gray-100" : ""}
                onClick={() => handleSort("publish_date")}
              >
                Date {sortField === "publish_date" && (sortOrder === "asc" ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />)}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={sortField === "title" ? "bg-gray-100" : ""}
                onClick={() => handleSort("title")}
              >
                Title {sortField === "title" && (sortOrder === "asc" ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />)}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className={sortField === "views" ? "bg-gray-100" : ""}
                onClick={() => handleSort("views")}
              >
                Popular {sortField === "views" && (sortOrder === "asc" ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />)}
              </Button>
            </div>
          </div>

          <div className="mb-6 text-gray-500 text-sm">
            Showing {filteredArticles.length > 0 ? startIndex + 1 : 0}-{Math.min(startIndex + ITEMS_PER_PAGE, filteredArticles.length)} of {filteredArticles.length} articles
          </div>

          {filteredArticles.length > 0 ? (
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {paginatedArticles.map((article) => (
                <Card 
                  key={article.id}
                  className="overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <Link to={`/articles/${article.slug}`}>
                    <div className="h-48 overflow-hidden bg-gray-100">
                      <img
                        src={article.header_image_url || "/lovable-uploads/bf9cdc94-db08-4840-bae6-869c2f0c41bb.png"}
                        alt={article.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    
                    <CardHeader className="pb-2">
                      <h3 className="font-bold text-xl text-corporate-primary-100 hover:text-corporate-secondary-100 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                    </CardHeader>
                    
                    <CardContent>
                      <p className="text-gray-600 text-sm line-clamp-3">
                        {truncateText(article.lead_text || "", 150)}
                      </p>
                    </CardContent>
                    
                    <CardFooter className="border-t pt-4 flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center">
                        {article.author?.profile_image_url ? (
                          <img
                            src={article.author.profile_image_url}
                            alt={`${article.author.first_name} ${article.author.last_name || ""}`}
                            className="w-6 h-6 rounded-full mr-2 object-cover"
                          />
                        ) : (
                          <User className="w-4 h-4 mr-2" />
                        )}
                        <span>
                          {article.author ? 
                            `${article.author.first_name} ${article.author.last_name || ""}` : 
                            "METRADE Team"}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{formatDate(article.publish_date || article.created_at)}</span>
                      </div>
                    </CardFooter>
                  </Link>
                </Card>
              ))}
            </motion.div>
          ) : (
            <div className="py-16 text-center">
              <h2 className="text-2xl font-medium text-gray-700 mb-2">No articles found</h2>
              <p className="text-gray-500">Try adjusting your search or filters.</p>
            </div>
          )}

          {totalPages > 1 && (
            <Pagination className="mt-12">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
                
                {Array.from({ length: totalPages }).map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    totalPages > 7 &&
                    pageNumber !== 1 &&
                    pageNumber !== totalPages &&
                    Math.abs(pageNumber - currentPage) > 2
                  ) {
                    if (pageNumber === 2 || pageNumber === totalPages - 1) {
                      return (
                        <PaginationItem key={pageNumber} className="hidden md:block">
                          <span className="flex h-9 w-9 items-center justify-center">...</span>
                        </PaginationItem>
                      );
                    }
                    return null;
                  }
                  
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    Math.abs(pageNumber - currentPage) <= 1
                  ) {
                    return (
                      <PaginationItem key={pageNumber}>
                        <PaginationLink
                          isActive={pageNumber === currentPage}
                          onClick={() => setCurrentPage(pageNumber)}
                        >
                          {pageNumber}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }
                  
                  return null;
                })}
                
                <PaginationItem>
                  <PaginationNext 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      </main>
      
      {!isMobile && <ChatOption />}
      <Footer />
    </div>
  );
};

export default Blog;
